import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiUserPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetArtistListInfinite } from 'services/artists';
import {
  IArtistExternalIdMutation,
  IArtistResponse,
  IArtistSearch,
} from 'services/artists/types';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';

enum ARTIST_COLUMNS {
  isBand,
  nickname,
  name,
  contact,
  status,
  externalProfiles,
  // type,
}

const artistsSearchInitialValues: IArtistSearch = {
  EAN: '',
  email: '',
  externalId: '',
  externalIdType: '',
  firstName: '',
  ISRC: '',
  lastName: '',
  nickname: '',
  phoneNumber: '',
};

const Artists = () => {
  const [filters, setFilters] = useState<IArtistSearch>(
    artistsSearchInitialValues
  );

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetArtistListInfinite(filters);

  const tableData = getNodes<IArtistResponse>(data?.pages.flat());

  const { t } = useTranslation();
  const navigate = useNavigate();
  const i18nextPrefix = 'artist';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const columnsVisibility = useTableColumnsVisibility(
    'Artists2',
    ARTIST_COLUMNS
  );

  const onSelectChange = (action: Action, state: State) => {
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/external`, {
      relative: 'path',
    });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          path=''
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={ARTIST_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix={i18nextPrefix}
                />
              </div>

              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={() => navigate('create/external', { replace: false })}>
                <Button.Text
                  text={t('button.create-what', { what: t('artist.what') })}
                />
                <Button.Icon icon={<PiUserPlus />} />
              </Button>
            </>
          }>
          <QueryFilter<IArtistSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={artistsSearchInitialValues}>
            <>
              <QueryFilter.Form<IArtistSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setFilters(values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='status'
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text
                    name='firstName'
                    label={t('input.label.firstName')}
                  />
                  <FormInput.Text
                    name='lastName'
                    label={t('input.label.lastName')}
                  />
                  <FormInput.Text
                    name='nickname'
                    label={t('input.label.nickname')}
                  />
                  <FormInput.Text
                    name='email'
                    label='Email'
                    type='email'
                    placeholder='example@mail.com'
                  />
                  <FormInput.Text
                    name='phoneNumber'
                    label={t('input.label.phoneNumber')}
                    placeholder='123 456 789'
                  />
                  <FormInput.Text
                    name='externalIdType'
                    label={t('input.label.externalIdType')}
                    placeholder='Spotify'
                  />
                  <FormInput.Text
                    name='externalId'
                    label={t('input.label.externalId')}
                  />
                  <FormInput.Text
                    name='isrc'
                    label='ISRC'
                    placeholder='USP031900001'
                  />
                  <FormInput.Text
                    name='ean'
                    label={t('input.label.ean')}
                    placeholder='1234567890'
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.STATUS.${STATUS[data.value as STATUS]}`)
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={select}
        columns={[
          {
            label: `${t(
              `${i18nextPrefix}.table.${
                ARTIST_COLUMNS[ARTIST_COLUMNS.nickname]
              }`
            )}`,
            renderCell: item => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Image
                    url={item.profilePicturePreviewLink}
                    alt={item.nickname}
                  />
                  <TableItem.Text text={item.nickname} />
                </div>
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(ARTIST_COLUMNS.nickname),
          },
          {
            label: t(
              `${i18nextPrefix}.table.${ARTIST_COLUMNS[ARTIST_COLUMNS.name]}`
            ),
            renderCell: item => (
              <TableItem>
                <TableItem.Text
                  text={`${item.name.prefix} ${item.name.firstName} ${item.name.lastName} ${item.name.suffix}`}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(ARTIST_COLUMNS.name),
          },

          {
            label: t(
              `${i18nextPrefix}.table.${ARTIST_COLUMNS[ARTIST_COLUMNS.contact]}`
            ),
            renderCell: ({ phoneNumber, email }) => (
              <TableItem className='gap-2'>
                <TableItem.Text text={email} />
                {phoneNumber ? (
                  <TableItem.Text
                    text={`${phoneNumber.prefix} ${phoneNumber.number}`}
                    isSupplementary
                  />
                ) : null}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(ARTIST_COLUMNS.contact),
          },

          {
            label: t(
              `${i18nextPrefix}.table.${
                ARTIST_COLUMNS[ARTIST_COLUMNS.externalProfiles]
              }`
            ),
            renderCell: item => (
              <TableItem>
                <TableItem.List<IArtistExternalIdMutation>
                  items={item.externalIds}>
                  {external => (
                    <TableItem.Text
                      isSupplementary
                      key={external.id}
                      text={`${external.type}`}
                    />
                  )}
                </TableItem.List>
              </TableItem>
            ),
            hide: columnsVisibility.hide(ARTIST_COLUMNS.externalProfiles),
            resize: true,
          },
        ]}
        displayMode
        loading={isLoading || isFetchingNextPage}
        data={tableData}
        onFullyScroll={fetchNextPage}
      />
    </TopbarPage>
  );
};

export default Artists;
